import { useMemo } from 'react';
import { type FormFieldProps } from '@direct-frontend/components';

type FormFieldErrorProps = Pick<FormFieldProps, 'validationState' | 'errorCode' | 'errorMessage'>;

/**
 * Хук для получения всех props про ошибки для компонента FormField
 *
 * @kind helper
 * @param state - состояние формы
 * @param getErrorMessage - функция для получения сообщения об ошибке (собирать с помощью factoryGetErrorMessage)
 * @typeParam Errors - тип ошибок формы
 * @returns {FormFieldErrorProps} - свойства ошибки
 *
 * @example
 * const errorProps = useFormFieldErrorProps(featureField.state, featureFieldGetErrorMessage);
 * <FormField {...errorProps}> ... </FormField>
 */
export function useFormFieldErrorProps<Errors extends { code: string }>(
    state: {
        isValid: boolean;
        errors: ReadonlyArray<{ code: string }>;
    },
    getErrorMessage: (error: Errors) => string
): FormFieldErrorProps {
    const { isValid, errors } = state;
    const error = errors[0] as Errors | undefined;

    return useMemo(() => {
        return {
            validationState: isValid ? 'valid' : 'invalid',
            errorCode: error?.code,
            errorMessage: error ? getErrorMessage(error) : undefined,
        };
    }, [isValid, error, getErrorMessage]);
}
